import React, { useState } from "react";
import { View, ScrollView, TouchableOpacity, Platform, Linking } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { PageFooter } from "@/components/ui/PageFooter";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import { suppressDevAuth } from "@/dev/DevAuthBootstrap";
import { cn } from "@/lib/utils";
import { ChangePasswordModal } from "./ChangePasswordModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(firstName: string, lastName: string): string {
  return firstName.trim().charAt(0).toUpperCase() + lastName.trim().charAt(0).toUpperCase();
}

// ─── Language segmented control ───────────────────────────────────────────────

interface LanguageSegmentedControlProps {
  locales: { locale: SupportedLocale; label: string }[];
  currentLocale: SupportedLocale;
  onSelect: (locale: SupportedLocale) => void;
  isWide: boolean;
}

function LanguageSegmentedControl({
  locales,
  currentLocale,
  onSelect,
  isWide,
}: LanguageSegmentedControlProps) {
  return (
    <View
      className={`items-start w-full flex-row flex-wrap gap-4 max-w-2xl ${isWide ? "px-10 py-6" : "px-4 py-5"}`}
    >
      {locales.map(({ locale, label }) => {
        const isActive = locale === currentLocale;
        return (
          <TouchableOpacity
            key={locale}
            onPress={() => onSelect(locale)}
            className={`flex-1 h-10 items-center justify-center rounded ${
              isActive ? "bg-primary" : "bg-stroke-20"
            }`}
            accessibilityRole="radio"
            accessibilityState={{ checked: isActive }}
            testID={`language-btn-${locale}`}
            activeOpacity={0.8}
          >
            <AppText
              weight={isActive ? "semibold" : "medium"}
              className={`text-base ${isActive ? "text-white" : "text-supporting-text"}`}
            >
              {label}
            </AppText>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ─── Menu nav row ─────────────────────────────────────────────────────────────

interface MenuRowProps {
  iconName: React.ComponentProps<typeof MaterialCommunityIcons>["name"];
  label: string;
  onPress: () => void;
  isWide: boolean;
  isDestructive?: boolean;
  showChevron?: boolean;
  testID?: string;
}

function MenuRow({
  iconName,
  label,
  onPress,
  isWide,
  isDestructive,
  showChevron = true,
  testID,
}: MenuRowProps) {
  const { isRTL } = useI18n();
  const textColor = isDestructive ? "text-error" : "text-dark-text-50";
  const iconColor = isDestructive ? "text-error" : "text-supporting-text-40";

  return (
    <TouchableOpacity
      onPress={onPress}
      className={`w-full flex-row items-center justify-between gap-3 ${isWide ? "px-10 py-6" : "px-4 py-[18px]"}`}
      accessibilityRole="button"
      activeOpacity={0.7}
      testID={testID}
    >
      <View className="items-center flex-row gap-3">
        <Icon as={MaterialCommunityIcons} name={iconName} size={20} className={iconColor} />
        <AppText weight="medium" className={`text-base ${textColor}`}>
          {label}
        </AppText>
      </View>
      {showChevron && (
        <Icon
          as={MaterialCommunityIcons}
          name={isRTL ? "chevron-left" : "chevron-right"}
          size={20}
          className="text-supporting-text-40"
        />
      )}
    </TouchableOpacity>
  );
}

// ─── Divider ──────────────────────────────────────────────────────────────────

function RowDivider() {
  return <View className="border-b border-stroke-50 w-full" />;
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export default function VendorProfileScreen() {
  const { t, locale, setLocale, availableLocales } = useI18n();
  const { user, logout } = useAuth();
  const { top } = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  const initials = user ? getInitials(user.firstName, user.lastName) : "?";
  const fullName = user ? `${user.firstName} ${user.lastName}` : "—";
  const cardClass = `bg-white overflow-hidden ${
    isWide ? "rounded-lg border border-stroke-50" : "border border-stroke-50 rounded-xl"
  }`;

  const handleLogout = () => {
    if (__DEV__) {
      suppressDevAuth();
    }
    logout();
  };

  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  return (
    <ScrollView
      className={`flex-1 ${isWide ? "bg-stroke-20" : "bg-white"}`}
      contentContainerStyle={{ flexGrow: 1 }}
      testID="vendor-profile"
    >
      {/* ── User info header ─────────────────────────────────────── */}
      {isWide && Platform.OS === "web" ? (
        /* Desktop: white band with avatar + bold name + email */
        <View className="bg-white border-b border-stroke-50 items-start w-full">
          <View className="flex-row items-center gap-4 px-20 py-6">
            <View
              className="size-12 rounded-full items-center justify-center"
              style={{ backgroundColor: "#0C2B66" }}
              testID="profile-avatar"
            >
              <AppText weight="semibold" className="text-2xl text-stroke-10">
                {initials}
              </AppText>
            </View>
            <View className="gap-1 items-start">
              <AppText
                weight="bold"
                className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
                testID="profile-fullname"
              >
                {fullName}
              </AppText>
              <AppText
                weight="medium"
                className="text-base text-supporting-text-40"
                testID="profile-email"
              >
                {user?.email ?? "—"}
              </AppText>
            </View>
          </View>
        </View>
      ) : (
        /* Mobile: avatar row in page-x padding, safe-area aware */
        <View
          className="flex-row items-center gap-4 px-page-x pb-6"
          style={{ paddingTop: top + 16 }}
        >
          <View
            className="size-12 rounded-full items-center justify-center"
            style={{ backgroundColor: "#09204D" }}
            testID="profile-avatar"
          >
            <AppText weight="semibold" className="text-2xl text-stroke-10">
              {initials}
            </AppText>
          </View>
          <View className="flex-1 gap-0.5 items-start">
            <AppText
              weight="semibold"
              className="text-2xl leading-[29px] tracking-[-0.5px] text-dark-text-50"
              testID="profile-fullname"
            >
              {fullName}
            </AppText>
            <AppText
              weight="medium"
              className="text-base text-supporting-text-40"
              testID="profile-email"
            >
              {user?.email ?? "—"}
            </AppText>
          </View>
        </View>
      )}

      {/* ── Content cards ────────────────────────────────────────── */}
      <View className={`gap-6 items-start w-full ${isWide ? "px-20 py-6" : "pb-8 px-4 gap-4"}`}>
        {/* ── Language card ─────────────────────────────────────── */}
        <View className={cn("items-start w-full", cardClass)} testID="profile-language-card">
          {/* Header row */}
          <View
            className={`${isWide ? "px-10 py-6" : "px-4 py-5"} items-start w-full border-b border-stroke-50`}
          >
            <AppText weight="semibold" className="text-base text-dark-text-50">
              {t("language.switcher.label")}
            </AppText>
          </View>
          {/* Segmented control */}
          <LanguageSegmentedControl
            locales={availableLocales}
            currentLocale={locale}
            onSelect={setLocale}
            isWide={isWide}
          />
        </View>

        {/* ── Actions card ──────────────────────────────────────── */}
        <View className={cn("items-start w-full", cardClass)} testID="profile-actions-card">
          <MenuRow
            iconName="lock-outline"
            label={t("profile.changePassword")}
            onPress={() => setChangePasswordVisible(true)}
            isWide={isWide}
            testID="profile-row-change-password"
          />
          <RowDivider />
          <MenuRow
            iconName="help-circle-outline"
            label={t("profile.getHelp")}
            onPress={() => {
              Linking.openURL("mailto:" + t("vendor.pendingReview.supportEmail"));
            }}
            isWide={isWide}
            testID="profile-row-get-help"
          />
        </View>

        {/* ── Log out card ──────────────────────────────────────── */}
        <View className={cn("items-start w-full", cardClass)} testID="profile-logout-card">
          <MenuRow
            iconName="logout-variant"
            label={t("common.logout")}
            onPress={handleLogout}
            isWide={isWide}
            isDestructive
            showChevron={false}
            testID="profile-logout-btn"
          />
        </View>
      </View>

      {/* ── Desktop page footer ───────────────────────────────────── */}
      {isWide && Platform.OS === "web" && <PageFooter />}

      {/* ── Change Password Modal ─────────────────────────────────── */}
      <ChangePasswordModal
        visible={changePasswordVisible}
        onDismiss={() => setChangePasswordVisible(false)}
      />
    </ScrollView>
  );
}
